import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getProfile, type AccountProfileResponse } from '../lib/api';
import {
  consumeOAuthHash,
  getSupabaseAccountProfile,
  getValidSession,
  sendSupabasePasswordReset,
  signInWithGoogle as startGoogleSignIn,
  signInWithPassword,
  signOutSupabase,
  signUpWithPassword,
  supabaseEnabled,
  type SupabaseAuthUser,
  type SupabaseSession,
  updateSupabaseDisplayName,
  updateSupabasePassword
} from '../lib/supabase';

type Profile = AccountProfileResponse & { displayName?: string; photoURL?: string };

type AuthContextValue = {
  enabled: boolean;
  loading: boolean;
  profileLoading: boolean;
  profileError: string | null;
  session: SupabaseSession | null;
  user: SupabaseAuthUser | null;
  profile: Profile | null;
  refreshProfile: () => Promise<void>;
  setCreditsRemaining: (credits: number) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<'signed-in' | 'confirm-email'>;
  signInWithGoogle: (nextPath?: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  sendPasswordReset: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function mergeProfileIdentity(user: SupabaseAuthUser, profile: AccountProfileResponse): Profile {
  return { ...profile, displayName: user.displayName ?? undefined, photoURL: user.photoURL ?? undefined };
}

async function loadAccountProfile(session: SupabaseSession): Promise<Profile> {
  let directError: unknown = new Error('Could not load the account balance.');

  // Credit display reads the signed-in user's own Supabase row directly through
  // SELECT-only RLS. This avoids making the header depend on a separate Node
  // request while the Node server remains authoritative for every credit write.
  try {
    return mergeProfileIdentity(session.user, await getSupabaseAccountProfile(session));
  } catch (error) {
    directError = error;
  }

  // Compatibility/fallback for deployments where the v2 RLS migration has not
  // been applied yet. The server path can also create the profile securely.
  try {
    return mergeProfileIdentity(session.user, await getProfile(session.access_token));
  } catch {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 650));
    try {
      return mergeProfileIdentity(session.user, await getSupabaseAccountProfile(session));
    } catch {
      throw directError;
    }
  }
}

function errorMessage(error: unknown) {
  return error instanceof Error && error.message ? error.message : 'Could not load the account balance. Please retry.';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [user, setUser] = useState<SupabaseAuthUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const requestId = useRef(0);

  const applySession = useCallback(async (next: SupabaseSession | null, loadProfile = true) => {
    const currentId = ++requestId.current;
    setSession(next);
    setUser(next?.user ?? null);
    if (!next) {
      setProfile(null); setProfileError(null); setProfileLoading(false); setLoading(false); return;
    }
    if (!loadProfile) { setLoading(false); return; }
    setProfileLoading(true); setProfileError(null);
    try {
      const serverProfile = await loadAccountProfile(next);
      if (currentId === requestId.current) setProfile(serverProfile);
    } catch (error) {
      if (currentId === requestId.current) setProfileError(errorMessage(error));
    } finally {
      if (currentId === requestId.current) { setProfileLoading(false); setLoading(false); }
    }
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const oauth = consumeOAuthHash();
        const current = oauth ?? await getValidSession();
        if (active) await applySession(current);
      } catch (error) {
        if (active) { setProfileError(errorMessage(error)); setLoading(false); }
      }
    })();
    return () => { active = false; requestId.current += 1; };
  }, [applySession]);

  const refreshProfile = useCallback(async () => {
    const current = await getValidSession(true);
    await applySession(current);
  }, [applySession]);

  const value = useMemo<AuthContextValue>(() => ({
    enabled: supabaseEnabled,
    loading,
    profileLoading,
    profileError,
    session,
    user,
    profile,
    refreshProfile,
    setCreditsRemaining: (credits) => setProfile((current) => current ? { ...current, credits } : current),
    signIn: async (email, password) => applySession(await signInWithPassword(email, password)),
    signUp: async (email, password) => {
      const result = await signUpWithPassword(email, password);
      if (result.session) { await applySession(result.session); return 'signed-in'; }
      return 'confirm-email';
    },
    signInWithGoogle: async (nextPath = '/tools') => { await startGoogleSignIn(nextPath); },
    updateDisplayName: async (displayName) => {
      if (!session) throw new Error('Sign in before updating your profile.');
      const cleaned = displayName.trim().replace(/\s+/g, ' ');
      if (cleaned.length < 2 || cleaned.length > 60) throw new Error('Use a display name between 2 and 60 characters.');
      const next = await updateSupabaseDisplayName(session, cleaned);
      setSession(next); setUser(next.user);
      setProfile((current) => current ? { ...current, displayName: cleaned } : current);
    },
    updatePassword: async (password) => {
      if (!session) throw new Error('The password recovery session has expired. Request a new reset link.');
      await updateSupabasePassword(session, password);
    },
    sendPasswordReset: async () => {
      if (!user?.email) throw new Error('No email address is available for this account.');
      await sendSupabasePasswordReset(user.email);
    },
    signOut: async () => { await signOutSupabase(session); await applySession(null); }
  }), [applySession, loading, profile, profileError, profileLoading, refreshProfile, session, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
